import React, { useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import { useSchool } from '../contexts/SchoolContext';
import { supabase } from '../lib/supabase';
import './AdminSchoolSettings.css';

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE = /^\+[0-9]{8,15}$/;
const URL_FIELDS = [
  ['website_url', 'Website URL'],
  ['map_url', 'Map link (opens Google Maps)'],
  ['map_embed_url', 'Map embed URL (shown on the Contact page)'],
  ['facebook_url', 'Facebook page'],
  ['twitter_url', 'Twitter / X profile'],
  ['instagram_url', 'Instagram profile']
];

// Lists of public content: each item has two text fields. Limits match the database checks.
export const CONTENT_LISTS = {
  statistics: {
    title: 'About page figures', item: 'figure', max: 8,
    help: 'Shown on the About page. Only enter figures the school has confirmed. No figures: the section is hidden.',
    fields: [{ key: 'value', label: 'Figure', max: 20, placeholder: '300+' }, { key: 'label', label: 'Label', max: 60, placeholder: 'Happy Students' }]
  },
  faqs: {
    title: 'Frequently asked questions', item: 'question', max: 50,
    help: 'Shown on the FAQ page (linked in the menu once there is at least one). Only publish answers the school has approved.',
    fields: [{ key: 'question', label: 'Question', max: 300 }, { key: 'answer', label: 'Answer', max: 3000, multiline: true }]
  },
  facilities: {
    title: 'Facilities', item: 'facility', max: 30,
    help: 'Shown on the Facilities page (linked in the menu once there is at least one). Only list facilities the school has.',
    fields: [{ key: 'name', label: 'Facility', max: 100, placeholder: 'Library' }, { key: 'description', label: 'Description', max: 1000, multiline: true }]
  }
};

const toForm = (row) => ({
  statistics: (row.statistics ?? []).map((s) => ({ value: s.value, label: s.label })),
  faqs: (row.faqs ?? []).map((f) => ({ question: f.question, answer: f.answer })),
  facilities: (row.facilities ?? []).map((f) => ({ name: f.name, description: f.description })),
  school_name: row.school_name ?? '',
  address: row.address ?? '',
  phones: (row.phones ?? []).map((p) => ({ tel: p.tel, display: p.display, whatsapp: !!p.whatsapp })),
  email_general: row.email_general ?? '',
  email_admissions: row.email_admissions ?? '',
  contact_message: row.contact_message ?? '',
  academic_year: row.academic_year ?? '',
  privacy_policy: row.privacy_policy ?? '',
  notification_recipients: (row.notification_recipients ?? []).join('\n'),
  ...Object.fromEntries(URL_FIELDS.map(([k]) => [k, row[k] ?? '']))
});

const blankToNull = (v) => (v.trim() === '' ? null : v.trim());

// Returns { errors, values } where values is the payload for the update.
export const validateSettings = (form) => {
  const errors = {};
  if (!form.school_name.trim()) errors.school_name = 'School name is required';
  if (!form.address.trim()) errors.address = 'Address is required';
  form.phones.forEach((p, i) => {
    if (!PHONE.test(p.tel.trim())) errors[`phone_${i}`] = 'Use the international form, for example +923001234567';
    else if (!p.display.trim()) errors[`phone_${i}`] = 'Enter how the number should be shown';
  });
  for (const key of ['email_general', 'email_admissions']) {
    if (form[key].trim() && !EMAIL.test(form[key].trim())) errors[key] = 'Enter a valid email address';
  }
  for (const [key] of URL_FIELDS) {
    if (form[key].trim() && !/^https:\/\/\S+$/.test(form[key].trim())) errors[key] = 'Must start with https://';
  }
  const recipients = form.notification_recipients.split(/[\s,;]+/).filter(Boolean);
  if (recipients.some((r) => !EMAIL.test(r))) errors.notification_recipients = 'Enter valid email addresses, one per line';
  if (recipients.length > 20) errors.notification_recipients = 'At most 20 recipients';
  if (form.academic_year.length > 20) errors.academic_year = 'At most 20 characters';
  if (form.privacy_policy.length > 20000) errors.privacy_policy = 'At most 20,000 characters';
  const lists = {};
  for (const [name, def] of Object.entries(CONTENT_LISTS)) {
    const items = form[name] ?? [];
    if (items.length > def.max) errors[name] = `At most ${def.max} items`;
    items.forEach((item, i) => {
      for (const f of def.fields) {
        const v = String(item[f.key] ?? '').trim();
        if (!v) errors[`${name}_${i}_${f.key}`] = `${f.label} is required (or remove this ${def.item})`;
        else if (v.length > f.max) errors[`${name}_${i}_${f.key}`] = `At most ${f.max} characters`;
      }
    });
    lists[name] = items.map((item) => Object.fromEntries(def.fields.map((f) => [f.key, String(item[f.key] ?? '').trim()])));
  }

  const values = {
    school_name: form.school_name.trim(),
    address: form.address.trim(),
    phones: form.phones.map((p) => ({ tel: p.tel.trim(), display: p.display.trim(), whatsapp: p.whatsapp })),
    email_general: blankToNull(form.email_general),
    email_admissions: blankToNull(form.email_admissions),
    contact_message: blankToNull(form.contact_message),
    academic_year: blankToNull(form.academic_year),
    privacy_policy: blankToNull(form.privacy_policy),
    ...lists,
    notification_recipients: recipients,
    ...Object.fromEntries(URL_FIELDS.map(([k]) => [k, blankToNull(form[k])]))
  };
  return { errors, values };
};

const AdminSchoolSettings = () => {
  const { isViewer } = useAuth();
  const { refresh } = useSchool();
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from('school_settings').select('*').eq('id', 1).maybeSingle();
      if (error || !data) {
        setMessage({ type: 'error', text: 'Failed to load school settings' });
        return;
      }
      setForm(toForm(data));
    })();
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setPhone = (index, patch) =>
    setForm((f) => ({ ...f, phones: f.phones.map((p, i) => (i === index ? { ...p, ...patch } : p)) }));

  const save = async (e) => {
    e.preventDefault();
    const { errors: found, values } = validateSettings(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setMessage({ type: 'error', text: 'Please fix the highlighted fields' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    const { data, error } = await supabase.from('school_settings').update(values).eq('id', 1).select('id');
    if (error) {
      setMessage({ type: 'error', text: 'Could not save. Please check the values and try again.' });
    } else if (!data || data.length === 0) {
      // Row-level security silently skips the update for roles that may not change settings.
      setMessage({ type: 'error', text: 'You do not have permission to change school settings' });
    } else {
      setMessage({ type: 'success', text: 'School settings saved' });
      refresh();
    }
    setSaving(false);
  };

  // Editor for one of the CONTENT_LISTS: edit, add, remove and reorder items.
  const updateItem = (name, index, patch) =>
    setForm((f) => ({ ...f, [name]: f[name].map((item, i) => (i === index ? { ...item, ...patch } : item)) }));
  const moveItem = (name, index, delta) =>
    setForm((f) => {
      const items = [...f[name]];
      const [item] = items.splice(index, 1);
      items.splice(index + delta, 0, item);
      return { ...f, [name]: items };
    });
  const contentList = (name) => {
    const def = CONTENT_LISTS[name];
    const items = form[name];
    return (
      <section key={name}>
        <h2>{def.title}</h2>
        <p className="help-text">{def.help}</p>
        {errors[name] && <span className="error-message">{errors[name]}</span>}
        {items.length === 0 && <p className="help-text">None yet.</p>}
        {items.map((item, i) => (
          <fieldset className="content-item" key={i}>
            <legend className="visually-hidden">{`${def.item} ${i + 1}`}</legend>
            {def.fields.map((f) => {
              const id = `${name}-${i}-${f.key}`;
              const err = errors[`${name}_${i}_${f.key}`];
              const common = {
                id,
                value: item[f.key],
                disabled: isViewer,
                maxLength: f.max,
                placeholder: f.placeholder,
                onChange: (e) => updateItem(name, i, { [f.key]: e.target.value }),
                'aria-invalid': !!err,
                'aria-describedby': err ? `${id}-error` : undefined
              };
              return (
                <div className="form-group" key={f.key}>
                  <label htmlFor={id}>{`${f.label} ${i + 1}`}</label>
                  {f.multiline ? <textarea rows={3} {...common} /> : <input {...common} />}
                  {err && <span id={`${id}-error`} className="error-message">{err}</span>}
                </div>
              );
            })}
            {!isViewer && (
              <div className="content-item-actions">
                <button type="button" onClick={() => moveItem(name, i, -1)} disabled={i === 0}>Move up</button>
                <button type="button" onClick={() => moveItem(name, i, 1)} disabled={i === items.length - 1}>Move down</button>
                <button type="button" onClick={() => set(name, items.filter((_, j) => j !== i))}>{`Remove ${def.item}`}</button>
              </div>
            )}
          </fieldset>
        ))}
        {!isViewer && items.length < def.max && (
          <button
            type="button"
            onClick={() => set(name, [...items, Object.fromEntries(def.fields.map((f) => [f.key, '']))])}
          >
            {`Add a ${def.item}`}
          </button>
        )}
      </section>
    );
  };

  const field = (key, label, props = {}) => (
    <div className="form-group">
      <label htmlFor={key}>{label}</label>
      <input id={key} value={form[key]} onChange={(e) => set(key, e.target.value)} disabled={isViewer}
        aria-invalid={!!errors[key]} aria-describedby={errors[key] ? `${key}-error` : undefined} {...props} />
      {errors[key] && <span id={`${key}-error`} className="error-message">{errors[key]}</span>}
    </div>
  );

  return (
    <AdminLayout>
      <div className="school-settings">
        <h1>School Settings</h1>
        <p className="subtitle">
          Contact details and links shown across the public website, and who is notified about new applications.
        </p>
        {message.text && <div className={`alert alert-${message.type}`} role="status">{message.text}</div>}
        {isViewer && <div className="alert alert-info">You can view these settings but not change them.</div>}

        {!form ? (
          !message.text && <div className="loading">Loading school settings...</div>
        ) : (
          <form onSubmit={save} className="settings-form" noValidate>
            <section>
              <h2>School</h2>
              {field('school_name', 'School name')}
              {field('address', 'Address')}
              {field('academic_year', 'Current academic year', { placeholder: 'For example 2026-27' })}
              <div className="form-group">
                <label htmlFor="contact_message">General contact message (optional)</label>
                <textarea id="contact_message" rows={3} value={form.contact_message} disabled={isViewer}
                  onChange={(e) => set('contact_message', e.target.value)} />
              </div>
            </section>

            <section>
              <h2>Phone numbers</h2>
              {form.phones.map((p, i) => (
                <div className="phone-row" key={i}>
                  <div className="form-group">
                    <label htmlFor={`phone-tel-${i}`}>Number {i + 1} (international)</label>
                    <input id={`phone-tel-${i}`} value={p.tel} disabled={isViewer} placeholder="+923001234567"
                      onChange={(e) => setPhone(i, { tel: e.target.value })} aria-invalid={!!errors[`phone_${i}`]} />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`phone-display-${i}`}>Shown as</label>
                    <input id={`phone-display-${i}`} value={p.display} disabled={isViewer} placeholder="0300 1234567"
                      onChange={(e) => setPhone(i, { display: e.target.value })} />
                  </div>
                  <label className="inline-check">
                    <input type="checkbox" checked={p.whatsapp} disabled={isViewer}
                      onChange={(e) => setPhone(i, { whatsapp: e.target.checked })} />
                    WhatsApp
                  </label>
                  {!isViewer && (
                    <button type="button" onClick={() => set('phones', form.phones.filter((_, j) => j !== i))}>Remove</button>
                  )}
                  {errors[`phone_${i}`] && <span className="error-message">{errors[`phone_${i}`]}</span>}
                </div>
              ))}
              {!isViewer && form.phones.length < 10 && (
                <button type="button" onClick={() => set('phones', [...form.phones, { tel: '', display: '', whatsapp: false }])}>
                  Add a number
                </button>
              )}
            </section>

            <section>
              <h2>Email</h2>
              {field('email_general', 'General email', { type: 'email' })}
              {field('email_admissions', 'Admissions email', { type: 'email' })}
              <div className="form-group">
                <label htmlFor="notification_recipients">
                  Notification recipients (new applications and enquiries), one email per line
                </label>
                <textarea id="notification_recipients" rows={3} value={form.notification_recipients} disabled={isViewer}
                  onChange={(e) => set('notification_recipients', e.target.value)}
                  aria-invalid={!!errors.notification_recipients} />
                {errors.notification_recipients && <span className="error-message">{errors.notification_recipients}</span>}
                <p className="help-text">Not shown on the website. Used by the email notifications.</p>
              </div>
            </section>

            {Object.keys(CONTENT_LISTS).map((name) => contentList(name))}

            <section>
              <h2>Privacy policy</h2>
              <div className="form-group">
                <label htmlFor="privacy_policy">Policy text (shown at /privacy)</label>
                <textarea id="privacy_policy" rows={10} value={form.privacy_policy} disabled={isViewer}
                  onChange={(e) => set('privacy_policy', e.target.value)} aria-invalid={!!errors.privacy_policy} />
                {errors.privacy_policy && <span className="error-message">{errors.privacy_policy}</span>}
                <p className="help-text">
                  Separate paragraphs with a blank line. Each change creates a new policy version; every application
                  records the version its parent accepted. Applicants must accept it, so the text should be approved
                  by the school before it is published.
                </p>
              </div>
            </section>

            <section>
              <h2>Links</h2>
              <p className="help-text">Leave a social link empty to hide its icon from the website footer.</p>
              {URL_FIELDS.map(([key, label]) => field(key, label, { type: 'url', placeholder: 'https://' }))}
            </section>

            {!isViewer && (
              <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : 'Save settings'}</button>
            )}
          </form>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSchoolSettings;
